// =============================================================================
// interface.ts — Platform SDK abstraction layer
// Project: Texas Hold'em Social Casino
// =============================================================================
// Defines the interface that all platform adapters must implement.
// The game uses this interface to interact with embedding platforms
// (CrazyGames, Poki, LINE, Facebook, etc.) without coupling to any.
//
// Each platform implements this interface and is selected at startup
// based on runtime detection (URL params, global variables, etc.).

export interface PlatformAdapter {
  /** Unique platform name identifier */
  readonly name: string

  /** Check if the platform SDK is loaded and ready */
  isReady(): boolean

  /** Show a rewarded video ad. Returns true if user watched until end. */
  showRewardedAd(): Promise<boolean>

  /** Show an interstitial ad (between screens). Resolves when ad closes. */
  showInterstitial(): Promise<void>

  /** Show a banner ad. Returns cleanup function. */
  showBanner(container: HTMLElement): () => void

  /** Check if running standalone (direct website, no embedding platform) */
  isStandalone(): boolean

  /** Get the platform's user ID (if authenticated) */
  getUserId(): string | null

  /** Check if in-app purchases are available on this platform */
  canPurchase(): boolean

  /** Purchase a SKU on the platform's payment system */
  purchase(sku: string): Promise<boolean>

  /** Share content via the platform's sharing mechanism */
  share(text: string): Promise<void>

  /** Track an analytics event */
  trackEvent(event: string, data?: Record<string, unknown>): void

  /** Get the player's locale string (e.g. 'en-US', 'ja-JP') */
  getLocale(): string
}

// ── Standalone (Direct Website) Adapter ──────────────────────────────────────
// Used when the game is accessed directly via the PWA website.
// Falls back to direct ad network integration.

export class StandaloneAdapter implements PlatformAdapter {
  name = 'standalone'
  private locale = navigator.language || 'en-US'

  isReady(): boolean { return true }

  async showRewardedAd(): Promise<boolean> {
    // TODO: Integrate AppLixir or Google Ad Manager rewarded ads
    console.log('[Standalone] Rewarded ad requested')
    return false
  }

  async showInterstitial(): Promise<void> {
    console.log('[Standalone] Interstitial ad requested')
  }

  showBanner(container: HTMLElement): () => void {
    console.log('[Standalone] Banner ad requested')
    return () => {}
  }

  isStandalone(): boolean { return true }
  getUserId(): string | null { return null }

  canPurchase(): boolean { return true }

  async purchase(sku: string): Promise<boolean> {
    // Direct Stripe integration
    console.log('[Standalone] Purchase:', sku)
    return false
  }

  async share(text: string): Promise<void> {
    if (navigator.share) {
      await navigator.share({ text })
    }
  }

  trackEvent(event: string, data?: Record<string, unknown>): void {
    console.log('[Analytics]', event, data)
  }

  getLocale(): string { return this.locale }
}

// ── Adapter Factory ─────────────────────────────────────────────────────────
// Detects the current platform and creates the appropriate adapter.

export function createPlatformAdapter(): PlatformAdapter {
  // Detect LINE LIFF
  if (typeof (window as any).liff !== 'undefined') {
    const { LineAdapter } = require('./line')
    return new LineAdapter()
  }

  // Detect CrazyGames
  if (typeof (window as any).CrazyGames !== 'undefined') {
    const { CrazyGamesAdapter } = require('./crazy-games')
    return new CrazyGamesAdapter()
  }

  // Detect Poki
  if (typeof (window as any).Poki !== 'undefined') {
    const { PokiAdapter } = require('./poki')
    return new PokiAdapter()
  }

  // Detect Facebook Instant Games
  if (typeof (window as any).FBInstant !== 'undefined') {
    const { FacebookAdapter } = require('./facebook')
    return new FacebookAdapter()
  }

  // Default to standalone
  return new StandaloneAdapter()
}
